import React, { useState, useCallback, useEffect } from 'react';

import { Switch, TableRow, ClickAwayListener } from '@material-ui/core';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import DeleteIcon from '@material-ui/icons/Delete';
// import { StyledIconButton1 } from './styles';
import ExpandMoreRoundedIcon from '@material-ui/icons/ExpandMoreRounded';
import { StyledTableCell } from 'screens/Team/TeamTasksListView/styles';
import styled, { css } from 'styled-components';
import { NewStyledIconButton, PaddingCSS, StyledTooltip } from 'styles/common';

// import { StyledTableRow, StyledTableCell, HeaderTableCell } from 'screens/Team/TeamTasksListView/styles';
import { getColorFromKey, haveSamePropValues } from 'constants/helper';
import { StyledIcon } from 'constants/icons';
import theme from 'constants/theme';

import ExpandedTableWithinCard from './ExpandedTableInCard';
import { StyledTextField } from './styles';

// helpers for this component only.
// keeping them here makes more sense than moving them up in helpers.
const MAX_EXPANSION_LEVEL = 4;

const getLeftPadding = (nestingLevel) => {
  const paddingFactor = 30;
  return `${nestingLevel * paddingFactor}px`;
};

export const StyledVerticalLine = styled.span`
  position: absolute;
  top: -15px;
  bottom: ${(props) => (props.bottom ? props.bottom : '-1px')};
  left: ${(props) => (props.left ? props.left : 0)};
  display: inline-block;
  width: 1px;
  background-color: ${getColorFromKey('primary')};
`;

export const StyledHorizontalLine = styled.span`
  position: absolute;
  left: 0;
  top: 50%;
  display: inline-block;
  height: 1px;
  width: ${(props) => props.width};
  background-color: ${getColorFromKey('primary')};
`;

const getVerticalLines = (nestingLevel, isCreateNewRow = false) => {
  if (nestingLevel === 0) return null;

  return (
    <>
      {Array.from(Array(nestingLevel - 1).keys()).map((num) => (
        <StyledVerticalLine left={`${(num + 1) * -30}px`} />
      ))}
      <StyledHorizontalLine width="20px" />
      <StyledVerticalLine left="0" bottom={isCreateNewRow ? '50%' : '-1px'} />
    </>
  );
};

const showExpansionIcon = (nestingLevel) => nestingLevel < MAX_EXPANSION_LEVEL;

const ExpansionControl = ({
  nestingLevel,
  handleExpansionClick, // pass functions using callback hook to save renders.
  hasChildTags,
  isCreateNewRow,
  isExpanded,
}) => (
  <>
    {getVerticalLines(nestingLevel, isCreateNewRow)}
    <StyledExpansionContainer>
      {showExpansionIcon(nestingLevel) && (
        <>
          {hasChildTags && !isExpanded && (
            <NewStyledIconButton padding="none" height="max-content" onClick={handleExpansionClick} color="primary">
              <StyledExpansionIcon size="header">
                <ExpandMoreRoundedIcon />
              </StyledExpansionIcon>
            </NewStyledIconButton>
          )}
          {hasChildTags && isExpanded && (
            <NewStyledIconButton padding="none" height="max-content" onClick={handleExpansionClick} color="primary">
              <StyledIcon size="header">
                <ExpandMoreRoundedIcon />
              </StyledIcon>
            </NewStyledIconButton>
          )}
        </>
      )}
    </StyledExpansionContainer>
  </>
);

const TagSwitch = ({ active, handleToggleActive, isEditing }) => (
  <StyledTableCell paddingTop={!isEditing && '0'} paddingBottom={!isEditing && '0'} border>
    <Switch checked={active} onChange={handleToggleActive} color="primary" />
  </StyledTableCell>
);

const HashTagComp = ({
  name,
  handleKeyDown,
  handleTagChange,
  validationError,
  isCreateNewRow,
  children,
  nestingLevel,
  isEditing,
  isExpandable,
}) => (
  <StyledTableCell paddingtop="0" paddingBottom="0" paddingLeft={getLeftPadding(nestingLevel)} display="flex" border>
    <FlexContainer paddingLeft={isCreateNewRow && isExpandable ? '40px' : '20px'}>
      {children}
      <TextFieldContainer isEditing={isEditing}>
        <HashTagText>#</HashTagText>
        <StyledTextField
          name="name"
          placeholder="tag"
          fullWidth
          variant="outlined"
          value={name}
          onKeyDown={(event) => handleKeyDown({ event })}
          onChange={handleTagChange}
          autoFocus
        />
      </TextFieldContainer>
      {validationError && <ErrorSubText center>Space and special charaters are not allowed</ErrorSubText>}
    </FlexContainer>
  </StyledTableCell>
);

const TagDescription = ({ description, handleKeyDown, handleOnChange, isEditing, allowDescriptionNewline = false }) => (
  <StyledTableCell paddingtop="0" paddingBottom="0" border>
    <StyledTooltip
      title={description}
      disableHoverListener={isEditing || !description}
      disableFocusListener={isEditing || !description}
    >
      <TextFieldContainer isEditing={isEditing}>
        <StyledTextField
          placeholder="Description"
          name="description"
          fullWidth
          margin="none"
          variant="outlined"
          value={description || ''}
          onKeyDown={(event) => handleKeyDown({ event, isMultiline: true })}
          onChange={handleOnChange}
          multiline={allowDescriptionNewline}
        />
      </TextFieldContainer>
    </StyledTooltip>
  </StyledTableCell>
);

const NewEditableTagRow = ({
  tag,
  onUpdate: updateTag,
  nestingLevel = 0,
  isExpandable,
  isEditing,
  toggleEditing,
  // being not active is considered as deleted.
  onActiveToggle,
  hasChildTags = true,
  canHaveChildTags = true,
  enableExpansion: enableParentExpansion = () => {},
  disableExpansion: disableParentExpansion = () => {},
  allowDescriptionNewline = false,
}) => {
  const isCreateNewRow = !canHaveChildTags;
  const [currentTag, setCurrentTag] = useState(tag);
  const [validationError, setValidationError] = useState(false);

  const [showExpandedCard, setShowExpandedCard] = useState(false);
  const [showChildTagCreationRow, setShowChildTagCreationRow] = useState(false);

  const handleExpansionClick = (event) => {
    event.stopPropagation();
    setShowExpandedCard((prev) => !prev);
  };

  const handleAddClick = (event) => {
    event.stopPropagation();
    setShowChildTagCreationRow(true);
  };

  const { active, name, description } = currentTag;

  const handleOnChange = useCallback(({ target: { name, value } }) => {
    setCurrentTag((oldTag) => ({ ...oldTag, [name]: value }));
  }, []);

  const handleToggleActive = useCallback(
    (event) => {
      onActiveToggle(event, tag);
    },
    [tag]
  );

  const handleTagChange = useCallback(
    ({ target }) => {
      const regex = /^[a-zA-Z0-9_\b\*]+$/;
      setValidationError(false);

      if (target.value === '' || regex.test(target.value)) {
        handleOnChange({ target });
      } else {
        setValidationError(true);
        setTimeout(() => setValidationError(false), 2500);
      }
    },
    [handleOnChange]
  );

  const handleKeyDown = ({ event: { keyCode, key, target } }) => {
    if (keyCode === 27) {
      // this will work since everytime update is passed up to parent.
      target.blur();
      cancelEditing();
    }
    if (key === 'Enter' && !allowDescriptionNewline) {
      updateTag(currentTag);
    }
  };

  const cancelEditing = () => {
    setCurrentTag(tag);
    if (isEditing) toggleEditing(tag.id);
  };

  // listen to changes in parent tags array.
  useEffect(() => {
    setCurrentTag(tag);
  }, [tag.active, tag.name, tag.description, tag.id, isEditing]);

  const enableEditing = useCallback(() => {
    if (!isEditing) toggleEditing(tag.id);
  }, [tag.id, isEditing]);

  const handleClickAway = useCallback(() => {
    const propertyList = ['name', 'description'];
    if (haveSamePropValues(tag, currentTag, propertyList)) cancelEditing();
    else updateTag(currentTag);
  }, [currentTag, updateTag]);

  return (
    <>
      <ClickAwayListener onClickAway={handleClickAway}>
        <StyledTableRow isEditing={isEditing} onClick={enableEditing}>
          {/* {
        isExpandable && 
          <ExpansionControl 
            nestingLevel={nestingLevel}
            handleExpansionClick={handleExpansionClick}
            handleAddChild={handleAddClick}
            hasChildTags={hasChildTags}
            canHaveChildTags={canHaveChildTags}
            isExpanded={showExpandedCard}
          />
      } */}
          {/* <TagSwitch
        isEditing={isEditing}
        active={active} 
        handleToggleActive={handleToggleActive} 
      /> */}
          <HashTagComp
            isEditing={isEditing}
            name={name}
            handleTagChange={handleTagChange}
            handleKeyDown={handleKeyDown}
            validationError={validationError}
            nestingLevel={nestingLevel}
            isCreateNewRow={isCreateNewRow}
            isExpandable={isExpandable}
          >
            {isExpandable && (
              <ExpansionControl
                nestingLevel={nestingLevel}
                handleExpansionClick={handleExpansionClick}
                handleAddChild={handleAddClick}
                hasChildTags={hasChildTags}
                isCreateNewRow={isCreateNewRow}
                canHaveChildTags={false}
                isExpanded={showExpandedCard}
              />
            )}
          </HashTagComp>
          <TagDescription
            isEditing={isEditing}
            description={description}
            handleKeyDown={handleKeyDown}
            handleOnChange={handleOnChange}
            allowDescriptionNewline={allowDescriptionNewline}
          />
          <StyledTableCell textAlign="right" paddingTop={!isEditing && '0'} paddingBottom={!isEditing && '0'} border>
            {isEditing ? (
              <SpaceAroundContainer>
                <NewStyledIconButton
                  circular
                  backgroundColor={theme.palette.background.paper}
                  onClick={() => updateTag(currentTag)}
                >
                  <StyledIcon size="header" color="primary">
                    <CheckIcon />
                  </StyledIcon>
                </NewStyledIconButton>
                <NewStyledIconButton
                  circular
                  backgroundColor={theme.palette.background.paper}
                  onClick={(event) => {
                    event.stopPropagation();
                    cancelEditing();
                  }}
                >
                  <StyledIcon size="header" color="red">
                    <CloseIcon />
                  </StyledIcon>
                </NewStyledIconButton>
              </SpaceAroundContainer>
            ) : (
              <NewStyledIconButton
                onClick={(event) => {
                  event.stopPropagation();
                  handleToggleActive(event);
                }}
              >
                <StyledIcon size="header" color="primary">
                  <DeleteIcon />
                </StyledIcon>
              </NewStyledIconButton>
            )}
          </StyledTableCell>
        </StyledTableRow>
      </ClickAwayListener>

      {(showExpandedCard || showChildTagCreationRow) && (
        <ExpandedTableWithinCard
          parentTag={tag}
          nestingLevel={nestingLevel + 1}
          showExpandedCard={showExpandedCard}
          enableParentExpansion={enableParentExpansion}
          disableParentExpansion={disableParentExpansion}
        />
      )}
    </>
  );
};

const HashTagText = styled.span`
  // padding-top: 6px;
  display: inline-block;
  padding-right: 2px;
`;

const ErrorSubText = styled.span`
  font-size: 12px;
  margin: -5px 0 0 0;
  display: inline-block;
  color: red;
  opacity: 0.75;
  line-height: 1rem;
`;

const StyledExpansionIcon = styled(StyledIcon)`
  transform: rotate(-90deg);
`;

const StyledExpansionContainer = styled.div`
  display: flex;
  align-items: center;
  ${PaddingCSS}
`;

const FlexContainer = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  ${PaddingCSS};
`;

const StyledTableRow = styled(TableRow)`
  ${(props) =>
    props.isEditing &&
    css`
      background-color: #e9f6ff;
    `}
`;

const TextFieldContainer = styled.div`
  display: flex;
  align-items: center;

  flex: 1;

  margin-top: 0.5rem;
  margin-bottom: 0.5rem;

  padding-left: 0.25rem;

  ${(props) =>
    props.isEditing &&
    css`
      background-color: ${props.theme.palette.background.paper};
    `}
`;

const SpaceAroundContainer = styled.div`
  display: flex;
  justify-content: space-around;
`;
export default NewEditableTagRow;
